import axios from "axios";
import { ensureValidAccessToken, refreshSession } from "./api";
import { getAccessToken, getRefreshToken } from "./authStorage";

/**
 * Client for the platform (Wappin) backend — subscriptions, refunds, revenue,
 * invoices and seller GST settings. Uses the same Super Admin session as the
 * auth API; the backend confirms the token with the auth service.
 */
export const PLATFORM_API_URL = (
  process.env.REACT_APP_PLATFORM_API_URL || "https://api.inaiy.com"
).replace(/\/+$/, "");

const platformApi = axios.create({
  baseURL: `${PLATFORM_API_URL}/api/v1/billing`,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

function withToken(config, token = getAccessToken()) {
  config.headers = config.headers || {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
}

platformApi.interceptors.request.use(async (config) => {
  try {
    await ensureValidAccessToken();
  } catch {
    // The auth client handles an unrecoverable session; send what we have.
  }
  return withToken(config);
});

platformApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status !== 401 || !original || original._retry || !getRefreshToken()) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      const token = await refreshSession();
      return platformApi(withToken(original, token));
    } catch {
      return Promise.reject(error);
    }
  },
);

/** Readable message from a backend error response. */
export function platformErrorMessage(error, fallback = "Request failed") {
  const data = error?.response?.data;
  const fieldErrors = data?.details?.errors;
  if (Array.isArray(fieldErrors) && fieldErrors.length) {
    return fieldErrors.map((e) => e.message).join(". ");
  }
  if (error?.response?.status === 401) {
    return "The billing API did not accept your session. Sign out and sign in again.";
  }
  if (error?.response?.status === 403) {
    return "Your admin account lacks permission for this action.";
  }
  return data?.message || (typeof data?.error === "string" ? data.error : null) || error?.message || fallback;
}

/** Fetch a PDF as an object URL (caller revokes it). */
export async function fetchPdfObjectUrl(path) {
  const response = await platformApi.get(path, { responseType: "blob" });
  return URL.createObjectURL(response.data);
}

export default platformApi;
